import {
  Children,
  Fragment,
  createContext,
  forwardRef,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type AnchorHTMLAttributes,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';

// Repository-local router for PatPro's current route shapes. This deliberately
// implements only the exports in this file; it is not a drop-in React Router
// replacement. New routing features must add focused behavior and abuse tests
// here before application code relies on them.

export type Location<State = unknown> = {
  pathname: string;
  search: string;
  hash: string;
  state: State | null;
  key: string;
};

export type NavigationType = 'POP' | 'PUSH' | 'REPLACE';
export type To =
  | string
  | {
      pathname?: string;
      search?: string;
      hash?: string;
    };

type NavigateOptions = {
  replace?: boolean;
  state?: unknown;
};

type SearchParamsInit =
  | string
  | URLSearchParams
  | string[][]
  | Record<string, string | string[]>;

type RouterSnapshot = {
  location: Location;
  action: NavigationType;
};

type RouterValue = RouterSnapshot & {
  navigate: (to: To | number, options?: NavigateOptions) => void;
};

const RouterContext = createContext<RouterValue | null>(null);
const RouteBaseContext = createContext('/');
const ParamsContext = createContext<Readonly<Record<string, string | undefined>>>({});

let keySequence = 0;
const memoryOrigin = ['http:', '', 'patpro-router.local'].join('/');

function nextKey(): string {
  keySequence += 1;
  return `patpro-${keySequence.toString(36)}`;
}

function assertRouter(value: RouterValue | null): RouterValue {
  if (!value) throw new Error('Router APIs must be used within BrowserRouter or MemoryRouter.');
  return value;
}

function controlCharacterPresent(value: string): boolean {
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint <= 31 || codePoint === 127) return true;
  }
  return false;
}

function toText(to: To): string {
  if (typeof to === 'string') return to;
  const pathname = to.pathname ?? '';
  const search = to.search
    ? to.search.startsWith('?') ? to.search : `?${to.search}`
    : '';
  const hash = to.hash
    ? to.hash.startsWith('#') ? to.hash : `#${to.hash}`
    : '';
  return `${pathname}${search}${hash}`;
}

function baseDirectory(base: string): string {
  if (base === '/') return '/';
  return `${base.replace(/\/+$/, '')}/`;
}

function internalUrl(
  to: To,
  current: Location,
  routeBase: string,
  origin: string,
): URL {
  const raw = toText(to);
  if (controlCharacterPresent(raw)) {
    throw new Error('Navigation target contains a control character.');
  }
  if (/^[\\/]{2}/.test(raw)) {
    throw new Error('Protocol-relative navigation targets are not allowed.');
  }

  const explicitScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(raw);
  const currentUrl = new URL(
    `${current.pathname}${current.search}${current.hash}`,
    origin,
  );
  const baseUrl = raw.startsWith('?') || raw.startsWith('#') || raw === ''
    ? currentUrl
    : new URL(baseDirectory(routeBase), origin);

  let parsed: URL;
  try {
    parsed = new URL(raw || currentUrl.href, baseUrl);
  } catch {
    throw new Error('Navigation target is not a valid URL.');
  }
  if (
    parsed.origin !== origin
    || (explicitScheme && parsed.protocol !== new URL(origin).protocol)
    || parsed.username
    || parsed.password
  ) {
    throw new Error('Navigation target must be an internal same-origin URL.');
  }
  return parsed;
}

function locationFromUrl(url: URL, state: unknown, key = nextKey()): Location {
  return {
    pathname: url.pathname || '/',
    search: url.search,
    hash: url.hash,
    state: state ?? null,
    key,
  };
}

function entryLocation(entry: string | Partial<Location>, origin: string): Location {
  if (typeof entry === 'string') {
    const seed: Location = {
      pathname: '/',
      search: '',
      hash: '',
      state: null,
      key: nextKey(),
    };
    return locationFromUrl(internalUrl(entry, seed, '/', origin), null, seed.key);
  }
  const seed: Location = {
    pathname: '/',
    search: '',
    hash: '',
    state: null,
    key: entry.key ?? nextKey(),
  };
  const target = {
    pathname: entry.pathname ?? '/',
    search: entry.search ?? '',
    hash: entry.hash ?? '',
  };
  return locationFromUrl(
    internalUrl(target, seed, '/', origin),
    entry.state ?? null,
    seed.key,
  );
}

export function MemoryRouter({
  children,
  initialEntries = ['/'],
  initialIndex,
}: {
  children?: ReactNode;
  initialEntries?: Array<string | Partial<Location>>;
  initialIndex?: number;
}) {
  const origin = memoryOrigin;
  const [initialMemory] = useState(() => {
    if (initialEntries.length === 0) {
      throw new Error('MemoryRouter requires at least one initial entry.');
    }
    const entries = initialEntries.map((entry) => entryLocation(entry, origin));
    const index = initialIndex ?? entries.length - 1;
    if (!Number.isInteger(index) || index < 0 || index >= entries.length) {
      throw new Error('MemoryRouter initialIndex is outside the history bounds.');
    }
    return { entries, index };
  });
  const memoryRef = useRef(initialMemory);

  const [snapshot, setSnapshot] = useState<RouterSnapshot>(() => ({
    location: initialMemory.entries[initialMemory.index]!,
    action: 'POP',
  }));

  const navigate = useCallback((to: To | number, options: NavigateOptions = {}) => {
    const memory = memoryRef.current;
    const entries = memory.entries;
    if (typeof to === 'number') {
      const nextIndex = Math.max(0, Math.min(entries.length - 1, memory.index + to));
      memory.index = nextIndex;
      setSnapshot({ location: entries[nextIndex]!, action: 'POP' });
      return;
    }
    const current = entries[memory.index]!;
    const url = internalUrl(to, current, '/', origin);
    const location = locationFromUrl(url, options.state);
    if (options.replace) {
      entries[memory.index] = location;
      setSnapshot({ location, action: 'REPLACE' });
    } else {
      entries.splice(memory.index + 1, entries.length, location);
      memory.index += 1;
      setSnapshot({ location, action: 'PUSH' });
    }
  }, [origin]);

  const value = useMemo<RouterValue>(
    () => ({ ...snapshot, navigate }),
    [navigate, snapshot],
  );

  return (
    <RouterContext.Provider value={value}>
      {children}
    </RouterContext.Provider>
  );
}

type BrowserHistoryState = {
  __patproRouter?: true;
  key?: string;
  state?: unknown;
};

function browserSnapshot(action: NavigationType): RouterSnapshot {
  const stored = window.history.state as BrowserHistoryState | null;
  const url = new URL(window.location.href);
  return {
    location: locationFromUrl(
      url,
      stored?.__patproRouter ? stored.state : stored,
      stored?.__patproRouter && stored.key ? stored.key : nextKey(),
    ),
    action,
  };
}

export function BrowserRouter({ children }: { children?: ReactNode }) {
  const [snapshot, setSnapshot] = useState<RouterSnapshot>(() => browserSnapshot('POP'));

  useEffect(() => {
    const stored = window.history.state as BrowserHistoryState | null;
    if (!stored?.__patproRouter) {
      window.history.replaceState(
        { __patproRouter: true, key: snapshot.location.key, state: stored },
        '',
        window.location.href,
      );
    }
    const onPopState = () => setSnapshot(browserSnapshot('POP'));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [snapshot.location.key]);

  const navigate = useCallback((to: To | number, options: NavigateOptions = {}) => {
    if (typeof to === 'number') {
      window.history.go(to);
      return;
    }
    const current = browserSnapshot('POP').location;
    const url = internalUrl(to, current, '/', window.location.origin);
    const location = locationFromUrl(url, options.state);
    const state: BrowserHistoryState = {
      __patproRouter: true,
      key: location.key,
      state: location.state,
    };
    const href = `${location.pathname}${location.search}${location.hash}`;
    if (options.replace) window.history.replaceState(state, '', href);
    else window.history.pushState(state, '', href);
    setSnapshot({ location, action: options.replace ? 'REPLACE' : 'PUSH' });
  }, []);

  const value = useMemo<RouterValue>(
    () => ({ ...snapshot, navigate }),
    [navigate, snapshot],
  );

  return (
    <RouterContext.Provider value={value}>
      {children}
    </RouterContext.Provider>
  );
}

type RouteProps = {
  path?: string;
  index?: boolean;
  element?: ReactNode;
};

export function Route(_props: RouteProps): null {
  return null;
}

type RouteMatch = {
  element: ReactNode;
  params: Record<string, string | undefined>;
  pathnameBase: string;
  score: number;
  order: number;
};

function safeDecode(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

function pathSegments(value: string): string[] {
  return value.split('/').filter(Boolean);
}

function matchRoute(
  props: RouteProps,
  pathname: string,
  parentBase: string,
  order: number,
): RouteMatch | null {
  const base = parentBase === '/' ? '/' : parentBase.replace(/\/+$/, '');
  const baseSegments = pathSegments(base);
  const pathnameSegments = pathSegments(pathname);
  const relativeSegments = pathnameSegments.slice(baseSegments.length);

  if (props.index) {
    if (relativeSegments.length !== 0) return null;
    return {
      element: props.element ?? null,
      params: {},
      pathnameBase: base,
      score: 1_000,
      order,
    };
  }

  const pattern = props.path ?? '';
  const absolute = pattern.startsWith('/');
  const candidate = absolute ? pathnameSegments : relativeSegments;
  const patternSegments = pathSegments(pattern);
  const params: Record<string, string | undefined> = {};
  const consumed: string[] = [];
  let score = absolute ? 5 : 0;
  let cursor = 0;

  for (const segment of patternSegments) {
    if (segment === '*') {
      const remainder = candidate.slice(cursor).join('/');
      const decoded = safeDecode(remainder);
      if (decoded === null) return null;
      params['*'] = decoded;
      score -= 100;
      cursor = candidate.length;
      break;
    }

    const optional = segment.startsWith(':') && segment.endsWith('?');
    if (segment.startsWith(':')) {
      const name = segment.slice(1, optional ? -1 : undefined);
      const value = candidate[cursor];
      if (value === undefined) {
        if (!optional) return null;
        params[name] = undefined;
        score += 4;
        continue;
      }
      const decoded = safeDecode(value);
      if (decoded === null) return null;
      params[name] = decoded;
      consumed.push(value);
      cursor += 1;
      score += optional ? 4 : 5;
      continue;
    }

    const value = candidate[cursor];
    const decodedValue = value === undefined ? null : safeDecode(value);
    const decodedPattern = safeDecode(segment);
    if (decodedValue === null || decodedPattern === null || decodedValue !== decodedPattern) {
      return null;
    }
    consumed.push(value!);
    cursor += 1;
    score += 10;
  }

  const hasWildcard = patternSegments.includes('*');
  if (!hasWildcard && cursor !== candidate.length) return null;

  const matchedSegments = absolute
    ? consumed
    : [...baseSegments, ...consumed];
  const pathnameBase = matchedSegments.length > 0 ? `/${matchedSegments.join('/')}` : '/';
  return {
    element: props.element ?? null,
    params,
    pathnameBase,
    score,
    order,
  };
}

function collectRoutes(children: ReactNode): ReactElement<RouteProps>[] {
  const result: ReactElement<RouteProps>[] = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === Fragment) {
      collectRoutes((child.props as { children?: ReactNode }).children)
        .forEach((route) => result.push(route));
    } else if (child.type === Route) {
      result.push(child as ReactElement<RouteProps>);
    }
  });
  return result;
}

export function Routes({ children }: { children?: ReactNode }) {
  const { location } = assertRouter(useContext(RouterContext));
  const parentBase = useContext(RouteBaseContext);
  const parentParams = useContext(ParamsContext);
  const routes = collectRoutes(children);
  const matches = routes
    .map((route, order) => matchRoute(route.props, location.pathname, parentBase, order))
    .filter((match): match is RouteMatch => match !== null)
    .sort((left, right) => right.score - left.score || left.order - right.order);
  const match = matches[0];
  if (!match) return null;

  return (
    <RouteBaseContext.Provider value={match.pathnameBase}>
      <ParamsContext.Provider value={{ ...parentParams, ...match.params }}>
        {match.element}
      </ParamsContext.Provider>
    </RouteBaseContext.Provider>
  );
}

export function useLocation<State = unknown>(): Location<State> {
  return assertRouter(useContext(RouterContext)).location as Location<State>;
}

export function useNavigationType(): NavigationType {
  return assertRouter(useContext(RouterContext)).action;
}

export function useNavigate(): RouterValue['navigate'] {
  const router = assertRouter(useContext(RouterContext));
  const routeBase = useContext(RouteBaseContext);
  return useCallback((to: To | number, options?: NavigateOptions) => {
    if (typeof to === 'number') {
      router.navigate(to, options);
      return;
    }
    const origin = typeof window === 'undefined'
      ? memoryOrigin
      : window.location.origin;
    const resolved = internalUrl(to, router.location, routeBase, origin);
    router.navigate(
      `${resolved.pathname}${resolved.search}${resolved.hash}`,
      options,
    );
  }, [routeBase, router]);
}

export function useParams<
  Params extends Record<string, string | undefined> = Record<string, string | undefined>,
>(): Readonly<Params> {
  return useContext(ParamsContext) as Readonly<Params>;
}

export function useSearchParams(): [
  URLSearchParams,
  (
    next:
      | SearchParamsInit
      | ((previous: URLSearchParams) => SearchParamsInit),
    options?: NavigateOptions,
  ) => void,
] {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const setParams = useCallback((
    next:
      | SearchParamsInit
      | ((previous: URLSearchParams) => SearchParamsInit),
    options?: NavigateOptions,
  ) => {
    const value = typeof next === 'function'
      ? next(new URLSearchParams(location.search))
      : next;
    const search = new URLSearchParams();
    if (typeof value === 'string' || value instanceof URLSearchParams || Array.isArray(value)) {
      new URLSearchParams(value as string | URLSearchParams | string[][])
        .forEach((item, key) => search.append(key, item));
    } else {
      Object.entries(value).forEach(([key, item]) => {
        if (Array.isArray(item)) item.forEach((entry) => search.append(key, entry));
        else search.set(key, item);
      });
    }
    const query = search.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}${location.hash}`, options);
  }, [location.hash, location.pathname, location.search, navigate]);
  return [params, setParams];
}

export function Navigate({
  to,
  replace,
  state,
}: {
  to: To;
  replace?: boolean;
  state?: unknown;
}) {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(to, { replace, state });
  }, [navigate, replace, state, to]);
  return null;
}

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  to: To;
  replace?: boolean;
  state?: unknown;
  reloadDocument?: boolean;
};

function useHref(to: To): string {
  const location = useLocation();
  const routeBase = useContext(RouteBaseContext);
  const origin = typeof window === 'undefined'
    ? memoryOrigin
    : window.location.origin;
  const url = internalUrl(to, location, routeBase, origin);
  return `${url.pathname}${url.search}${url.hash}`;
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  {
    to,
    replace,
    state,
    reloadDocument,
    onClick,
    target,
    children,
    ...anchorProps
  },
  ref,
) {
  const href = useHref(to);
  const navigate = useNavigate();
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (
      event.defaultPrevented
      || reloadDocument
      || event.button !== 0
      || event.metaKey
      || event.altKey
      || event.ctrlKey
      || event.shiftKey
      || (target && target !== '_self')
    ) return;
    event.preventDefault();
    navigate(href, { replace, state });
  };
  return (
    <a
      {...anchorProps}
      ref={ref}
      href={href}
      target={target}
      onClick={handleClick}
    >
      {children}
    </a>
  );
});

type NavLinkProps = Omit<LinkProps, 'className' | 'children'> & {
  end?: boolean;
  className?: string | ((state: { isActive: boolean }) => string | undefined);
  children?: ReactNode | ((state: { isActive: boolean }) => ReactNode);
};

export const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(function NavLink(
  { end = false, className, children, 'aria-current': ariaCurrent, ...props },
  ref,
) {
  const href = useHref(props.to);
  const location = useLocation();
  const targetPath = new URL(href, memoryOrigin).pathname.replace(/\/+$/, '') || '/';
  const currentPath = location.pathname.replace(/\/+$/, '') || '/';
  const isActive = currentPath === targetPath
    || (!end && targetPath !== '/' && currentPath.startsWith(`${targetPath}/`));
  const state = { isActive };
  return (
    <Link
      {...props}
      ref={ref}
      aria-current={ariaCurrent ?? (isActive ? 'page' : undefined)}
      className={typeof className === 'function' ? className(state) : className}
    >
      {typeof children === 'function' ? children(state) : children}
    </Link>
  );
});
